<template>
  <div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: space-between; flex-direction: column;">
    <div
      ref="svg"
      id="svgRoot"
      class="fill-container"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
        @click="(args)=>$emit('click', args)"
        :width="svgWidth"
        :height="svgHeight"
        :viewBox="`${-svgWidth/2} ${-svgHeight/2} ${svgWidth} ${svgHeight}`"
        style="overflow: visible;"
        id="capwheel"
      >
        <template v-if="loaded">
          <defs>
            <filter id="blur4">
              <feGaussianBlur stdDeviation="4" />
            </filter>

            <!-- #fff to #ccc linear gradient -->
            <linearGradient id="subcap-outline-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stop-color="white" />
              <stop offset="100%" stop-color="black" />
            </linearGradient>

            <linearGradient id="subcap-outline-gradient-flipped" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stop-color="black" />
              <stop offset="100%" stop-color="white" />
            </linearGradient>
          </defs>
          <text fill="transparent" ref="text-test-element" />
          <!-- container -->
          <g>
            <circle
              v-if="!disableShadow"
              id="outer-circle-shadow"
              :cx="circleOrigin[0] + 4"
              :cy="circleOrigin[1] + 4"
              :r="outerCircleRadius"
              filter="url(#blur4)"
              fill="rgba(0,0,0,0.5)"
            />

            <circle
              id="outer-circle"
              :cx="circleOrigin[0]"
              :cy="circleOrigin[1]"
              :r="outerCircleRadius"
              fill="#EFEFF0"
            />
          </g>

          <circle
            id="inner-circle"
            :cx="circleOrigin[0]"
            :cy="circleOrigin[1]"
            :r="innerCircleRadius"
            fill="rgba(255, 255, 255, 1)"
          />

          <g>
            <!-- <circle
              id="capability-circle-underlay"
              :cx="circleOrigin[0]"
              :cy="circleOrigin[1]"
              :r="capabilitySliceRadius + 2"
              :fill="categoryBorderColor || 'transparent'"
            /> -->

            <mask :id="`capwheel-radial-mask-caps-${id}`">
              <circle
                id="capability-circle-underlay"
                :cx="circleOrigin[0]"
                :cy="circleOrigin[1]"
                :r="capabilitySliceRadius"
                fill="white"
              />
            </mask>

            <g v-for="(data, capIdx) in capabilityDrawData" :key="capIdx">
              <!-- Radial gradient from white to the capability color -->
              <defs>
                <radialGradient
                  :id="`capwheel-radial-gradient-${capIdx}-${id}`"
                  cx="50%"
                  cy="50%"
                  r="50%"
                  fx="50%"
                  fy="50%"
                >
                  <stop :offset="0" stop-color="white" />
                  <stop :offset="1" :stop-color="data.color" />
                </radialGradient>
              </defs>

              <g :mask="`url(#capwheel-radial-mask-caps-${id})`">
                <!-- Display subcapabilities -->
                <g
                  v-for="(subcap, scIdx) in data.subcapabilities"
                  :key="`subcap-${scIdx}-${id}`"
                >
                  <!-- Linear path from circle origin to the subcapability for the label -->
                  <path
                    :id="`subcap-label-line-${scIdx}-${id}-${capIdx}`"
                    :d="`M${subcap.originPoint[0]} ${subcap.originPoint[1]} ${subcap.radiusPoint[0]} ${subcap.radiusPoint[1]}`"
                    stroke="transparent"
                  />

                  <!-- Subcapability fills -->
                  <g v-for="(arc, arcIdx) in subcap.arcs" :key="`subcap-arc-${arcIdx}-${id}-${capIdx}-${scIdx}`">
                    <path
                      :d="filledArcToPath(arc.arc)"
                      :fill="arc.color"
                      stroke="transparent"
                    />
                  </g>

                  <g v-for="(arc, arcIdx) in subcap.valueArcs" :key="`subcap-value-arc-${arcIdx}-${id}-${capIdx}-${scIdx}`">
                    <path
                      :d="filledArcToPath(arc.arc)"
                      :fill="arc.color"
                      stroke="transparent"
                    />
                  </g>

                  <!-- Subcapability labels -->
                  <!-- replaced dominant-baseline="middle" with dy because Safari (as usual) is broken -->
                  <text
                      :fill="innerLabelColor || 'white'"
                      font-size="10px"
                      dy="3px"
                      :text-anchor="subcap.isFlipped ? 'start' : 'end'"
                  >
                    <textPath
                      v-bind="{
                        'xlink:href': `#subcap-label-line-${scIdx}-${id}-${capIdx}`,
                      }"
                      :startOffset="subcap.isFlipped ? '5%' : '95%'"
                    >
                      {{subcap.label}}
                    </textPath>
                  </text>
                </g>
              </g>

              <!-- Top level capability text curve -->
              <path
                :d="curveToPath(data.textCurve) + 'f'"
                :id="`text-curve-${capIdx}-${id}`"
                fill="transparent"
                :stroke="debuggingTextPath ? 'red' : 'transparent'"
                stroke-width="1"
              />
              <!-- draw control points -->
              <template v-if="debuggingTextPath">
                  <circle
                    v-for="(pt, ptIdx) in data.textCurve"
                    :key="`text-curve-ctrl-${ptIdx}-${id}`"
                    :cx="pt[0]"
                    :cy="pt[1]"
                    r="5"
                    :fill="ptIdx === 0 || ptIdx === 3 ? 'red' : 'blue'"
                  />
              </template>

              <!-- Subcapability Outlines -->
              <g :mask="`url(#capwheel-radial-mask-caps-${id})`">
                <g v-for="(subcap, scIdx) in data.subcapabilities" :key="`subcap-arc-outline-${id}-${capIdx}-${scIdx}`">
                  <!-- Line from origin to arc start with linear  -->
                  <line
                    :x1="circleOrigin[0]"
                    :y1="circleOrigin[1]"
                    :x2="subcap.arcs[0].arc[1][0]"
                    :y2="subcap.arcs[0].arc[1][1]"
                    fill="transparent"
                    :stroke="
                      ((categoryBorderColor || data.isTop) && scIdx === 0)
                        ? (categoryBorderColor || 'rgba(255, 255, 255, 1)')
                        : 'rgba(255, 255, 255, 0.5)'
                    "
                    :stroke-width="
                      (data.isTop && scIdx === 0)
                        ? '6'
                        : '2'
                    "
                  />
                  <line
                    v-if="(categoryBorderColor ||data.isTop) && scIdx === (data.subcapabilities.length - 1)"
                    :x1="circleOrigin[0]"
                    :y1="circleOrigin[1]"
                    :x2="subcap.arcs[0].arc[2][0]"
                    :y2="subcap.arcs[0].arc[2][1]"
                    fill="transparent"
                    :stroke="categoryBorderColor || 'rgba(255, 255, 255, 1)'"
                    :stroke-width="data.isTop ? '6' : '2'"
                  />
                  <path
                    :d="arcToPath(subcap.arcs[0].arc)"
                    fill="transparent"
                    :stroke="categoryBorderColor || 'rgba(255, 255, 255, 0.5)'"
                    stroke-width="3"
                  />
                </g>
              </g>

              <!-- replaced dominant-baseline="middle" with dy because Safari (as usual) is broken -->
              <text
                text-anchor="middle"
                dy="6px"
                :font-size="outerLabelSize || '17px'"
                font-weight="bold"
                :fill="data.color"
              >
                  <textPath
                    v-bind="{
                      'xlink:href': `#text-curve-${capIdx}-${id}`,
                    }"
                    startOffset="50%"
                  >
                    {{data.label}}
                  </textPath>
              </text>
            </g>
          </g>
        </template>
      </svg>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-unused-vars */
import ElementQueries from 'css-element-queries';
import interpolate from 'color-interpolate';
import Color from 'color';

function rgbToRGBA(rgbColor, alpha) {
  const matches = rgbColor.match(/\d+/g);
  if (!matches) return rgbColor;
  const [r, g, b] = matches.map(Number);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

let globalIdAlloc = 0;

// type Point = [number, number];
// type Normal = [number, number];
// type Triangle = [Point, Point, Point];

// // start point, control point 1, control point 2, end point
// type Curve = [Point, Point, Point, Point];

// // radius, start point, end point
// type Arc = [number, Point, Point];

export default {
  props: {
    /** expects ```{ name, color, isTop, subcapabilities: { name, value (normalized) }[] }[]``` */
    capabilities: Array,
    disableShadow: Boolean,
    disableNontopGreyout: Boolean, // if set, isTop won't change the color
    outerLabelSize: String,
    innerLabelColor: String,
    categoryBorderColor: String,
    flippedFill: Boolean,
    numArcs: Number,
  },
  data() {
    return {
      id: -1,

      textTestElement: null, // SVGTextElement

      debuggingTextPath: false,
      debuggingSubcapabilityArcs: true,

      resizeSensor: null, // ElementQueries.ResizeSensor
      svgWidth: 0,
      svgHeight: 0,

      loaded: false,

      runningAnimIdx: 0,
      drawAngle: 0,
    };
  },
  computed: {
    unscaledRadialMaskTrianglesRaw() {
      return this.computeUnscaledRadialBoundingTriangles(0, this.drawAngle);
    },
    radialMaskTrianglesRaw() {
      const scale = this.outerCircleRadius;
      return this.unscaledRadialMaskTrianglesRaw.map(tri => tri.map(pt => [pt[0] * scale, pt[1] * scale]));
    },
    radialMaskTrianglesForSVG() {
      const cOrigin = this.circleOrigin;
      return this.radialMaskTrianglesRaw.map(tri => tri.map(pt => `${pt[0] + cOrigin[0]},${pt[1] + cOrigin[1]}`).join(' '));
    },
    circleOrigin() {
      return [0, 0];
    },
    outerCircleRadius() {
      return Math.min(this.svgWidth, this.svgHeight) / 2;
    },
    fillCircleRadius() {
      return this.outerCircleRadius;
    },
    /** NOTE: the fill circle is not being used outside the dashboard so "inner circle" is the whole circle */
    innerCircleRadius() {
      return this.outerCircleRadius;
    },
    capabilitySliceRadius() {
      return this.innerCircleRadius - 28;
    },
    capabilityDrawData() {
      // const values: {
      //     label: string,
      //     color: string,
      //     isTop: boolean,
      //     triangles: Triangle[],
      //     textCurve: Curve
      //     subcapabilities: {
      //         label: string,
      //         value: number,
      //         originPoint: Point,
      //         radiusPoint: Point,
      //         arcs: { color: string, arc: Arc }[],
      //         valueArcs: { color: string, arc: Arc }[],
      //         isFlipped: boolean
      //     }[]
      // }[] = [];
      const values = [];
      const topLevel = [...this.capabilities];

      const numTopLevel = topLevel.length;
      const increment = 1 / numTopLevel;
      // console.log({numTopLevel, increment});

      for (let currentTopLevelIdx = 0; currentTopLevelIdx < numTopLevel; ++currentTopLevelIdx) {
        const cap = topLevel[currentTopLevelIdx];
        const triangles = this.transformTriangles(
          this.computeUnscaledRadialBoundingTriangles(currentTopLevelIdx * increment, (currentTopLevelIdx + 1) * increment),
          this.capabilitySliceRadius,
          this.circleOrigin
        );

        // eslint-disable-next-line no-continue
        if (!triangles) continue;

        const start = currentTopLevelIdx * increment;
        const end = (currentTopLevelIdx + 1) * increment;
        const rawPath = this.computeRawSliceArcAsCurve(currentTopLevelIdx, numTopLevel);
        const radius = (this.capabilitySliceRadius + this.outerCircleRadius) / 2 * 0.995;
        const path = rawPath.map(pt => this.transformPoint(pt, radius, this.circleOrigin)); // Curve
        if (start > 0.24 && start < 0.74) {
          path.reverse();
        }

        // console.log({i: currentTopLevelIdx, start, end});

        let localSubcapIdx = 0;
        const capCopy = cap;
        values.push({
          label: cap.name,
          color: cap.color,
          isTop: cap.isTop,
          triangles,
          textCurve: path,
          // eslint-disable-next-line no-loop-func
          subcapabilities: cap.subcapabilities.map((subcap) => {
            // eslint-disable-next-line no-plusplus
            const currentLocalSubcapIdx = localSubcapIdx++;
            // eslint-disable-next-line no-plusplus
            const label = subcap.name;
            const value = subcap.value; // should already be normalized
            console.log(subcap);

            const interpolator = (currentLocalSubcapIdx / cap.subcapabilities.length) + 1 / cap.subcapabilities.length / 2;
            const radiusPerc = start + (end - start) * interpolator;
            const rawRadiusPoint = this.computeRawSlicePoint(radiusPerc);
            const radiusPoint = this.transformPoint(rawRadiusPoint, this.capabilitySliceRadius, this.circleOrigin);
            const originPoint = this.circleOrigin;

            const numRadialArcs = this.numArcs || 9;
            let baseColor = capCopy.color;
            if (!(this.disableNontopGreyout || capCopy.isTop)) {
              const color = Color(baseColor);
              baseColor = color.desaturate(0.3).whiten(0.22).hex();
            }
            console.log(baseColor);

            // Interpolate color between white and the capability color
            // both arrays are { color: string, arc: Arc}[]
            const arcs = [];
            const valueArcs = [];

            const localArcStart = (currentLocalSubcapIdx) / cap.subcapabilities.length;
            const localArcEnd = (currentLocalSubcapIdx + 1) / cap.subcapabilities.length;
            const globalArcStart = start + localArcStart * (end - start);
            const globalArcEnd = start + localArcEnd * (end - start);

            // Decrement index for proper ordering
            for (let arcIdx = numRadialArcs - 1; arcIdx >= 0; --arcIdx) {
              const rawInterpolator = arcIdx / (numRadialArcs - 1);
              const valueFillRadius = rawInterpolator * this.capabilitySliceRadius;
              const arc = this.computeTransformedArc(this.circleOrigin, valueFillRadius, globalArcStart, globalArcEnd);

              if (this.flippedFill) {
                const minInterpVal = 0;
                const maxInterpVal = 0.93;
                const colorInterpolator = rawInterpolator * (maxInterpVal - minInterpVal) + minInterpVal;
                const color = interpolate(['#c4c4c4', 'white'])(colorInterpolator);
                arcs.push({ color, arc });
              } else {
                // Bias the interpolation as we don't want pure white
                const bias = 0.5;
                const colorInterpolator = ((rawInterpolator) + bias) / (1 + bias);
                const color = interpolate(['white', baseColor])(colorInterpolator);

                arcs.push({ color, arc });
              }
            }

            const drawNotchedValueArcs = this.flippedFill;
            if (drawNotchedValueArcs) {
              const numValueArcs = Math.floor((numRadialArcs - 1) * value);
              for (let arcIdx = numValueArcs; arcIdx >= 0; --arcIdx) {
                const rawInterpolator = arcIdx / (numRadialArcs - 1);
                const valueFillRadius = rawInterpolator * this.capabilitySliceRadius;
                const arc = this.computeTransformedArc(this.circleOrigin, valueFillRadius, globalArcStart, globalArcEnd);

                if (this.flippedFill) {
                  const minInterpVal = 0;
                  const maxInterpVal = 0.4;
                  const colorInterpolator = (rawInterpolator - 0.2) * (maxInterpVal - minInterpVal) + minInterpVal;
                  const color = interpolate([baseColor, 'white'])(colorInterpolator);
                  valueArcs.push({ color: rgbToRGBA(color, 0.7), arc });
                } else {
                  const minInterpVal = 0.1;
                  const maxInterpVal = 0.7;
                  const colorInterpolator = rawInterpolator * (maxInterpVal - minInterpVal) + minInterpVal;
                  const color = interpolate(['#666', baseColor])(colorInterpolator);
                  valueArcs.push({ color: rgbToRGBA(color, 0.7), arc });
                }
              }
            } else {
              const rawInterpolator = value;
              const valueFillRadius = rawInterpolator * this.capabilitySliceRadius;
              const arc = this.computeTransformedArc(this.circleOrigin, valueFillRadius, globalArcStart, globalArcEnd);

              if (this.flippedFill) {
                // Bias the interpolation as we don't want pure white
                const bias = -0.5;
                const colorInterpolator = ((rawInterpolator) + bias) / (1 + bias);
                const color = interpolate([baseColor, 'white'])(colorInterpolator);

                valueArcs.push({ color: rgbToRGBA(color, 0.9), arc });
              } else {
                const color = interpolate(['#333', baseColor])(0.2);
                valueArcs.push({ color: rgbToRGBA(color, 0.4), arc });
              }
            }

            const res = { label, value, originPoint, radiusPoint, arcs, valueArcs, isFlipped: false };
            if (radiusPerc > 0.5) {
              const old = res.originPoint;
              res.originPoint = res.radiusPoint;
              res.radiusPoint = old;
              res.isFlipped = true;
            }
            return res;
          }),
        });
      }
      // console.log(values);
      return values;
    },
    displayAngle() {
      return Math.round(this.drawAngle * 100);
    }
  },
  methods: {
    computeRawSlicePoint(perc) {
      const angleOffset = Math.PI / 2 * 3;
      const twoPi = Math.PI * 2;
      const angle = perc * twoPi + angleOffset;
      return [Math.cos(angle), Math.sin(angle)];
    },
    computeRawSliceTangent(perc) {
      const angleOffset = Math.PI / 2 * 3;
      const twoPi = Math.PI * 2;
      const angle = perc * twoPi + angleOffset;

      // Tangent = x,y derivatives.
      // Derivative of sin is cos, and derivative of cos is -sin
      return [-Math.sin(angle), Math.cos(angle)];
    },
    computeRawSliceArcAsCurve(arcn, numArcs) {
      const startPerc = arcn / numArcs;
      const endPerc = (arcn + 1) / numArcs;
      const startPoint = this.computeRawSlicePoint(startPerc);
      const endPoint = this.computeRawSlicePoint(endPerc);
      const startTangent = this.computeRawSliceTangent(startPerc);
      const endTangent = this.computeRawSliceTangent(endPerc);

      // Based on https://stackoverflow.com/a/27863181
      const ctrlPtDist = (4 / 3) * Math.tan(Math.PI / (2 * numArcs));
      const ctrlPtOne = [startPoint[0] + ctrlPtDist * startTangent[0], startPoint[1] + ctrlPtDist * startTangent[1]];
      const ctrlPtTwo = [endPoint[0] - ctrlPtDist * endTangent[0], endPoint[1] - ctrlPtDist * endTangent[1]];

      return [startPoint, ctrlPtOne, ctrlPtTwo, endPoint];
    },
    computeTransformedArc(circleOrigin, circleRadius, startPerc, endPerc) {
      const startPoint = this.transformPoint(this.computeRawSlicePoint(startPerc), circleRadius, circleOrigin);
      const endPoint = this.transformPoint(this.computeRawSlicePoint(endPerc), circleRadius, circleOrigin);
      return [circleRadius, startPoint, endPoint];
    },
    arcToPath(arc) {
      const [radius, start, end] = arc;
      const largeArcFlag = 0;
      return `M${start.join(' ')} A${radius} ${radius} 0 ${largeArcFlag} 1 ${end.join(' ')}`;
    },
    filledArcToPath(arc) {
      const circleOrigin = this.circleOrigin;
      const [radius, start, end] = arc;
      const largeArcFlag = 0;

      // Arc, but include a line to and from the center point
      return `M${circleOrigin.join(' ')} L${start.join(' ')} A${radius} ${radius} 0 ${largeArcFlag} 1 ${end.join(' ')} L${circleOrigin.join(' ')}`;
    },
    computeRawSliceStartEndPoint(startPerc, endPerc, minRange = 0, maxRange = 1) {
      const angleOffset = Math.PI / 2 * 3;
      const twoPi = Math.PI * 2;

      // Note: These additions are a hack to fix the seams.  Need to figure out what's actually going on there.
      const minAngle = Math.max(minRange, startPerc) * twoPi + angleOffset - 0.01;
      const maxAngle = Math.min(maxRange, endPerc) * twoPi + angleOffset + 0.01;

      const p1 = [Math.cos(minAngle), Math.sin(minAngle)];
      const p2 = [Math.cos(maxAngle), Math.sin(maxAngle)];

      // Project both points on the bounding square
      const p1Len = Math.max(Math.abs(p1[0]), Math.abs(p1[1]));
      const p2Len = Math.max(Math.abs(p2[0]), Math.abs(p2[1]));

      p1[0] /= p1Len;
      p1[1] /= p1Len;
      p2[0] /= p2Len;
      p2[1] /= p2Len;
      return [p1, p2];
    },
    computeRawSliceMaskTriangle(triId, minClipPerc, maxClipPerc) {
      const minRange = triId / 8;
      const maxRange = (triId + 1) / 8;

      if ((minClipPerc) > maxRange) return null;
      if ((maxClipPerc) < minRange) return null;

      return ([[0, 0], ...this.computeRawSliceStartEndPoint(minClipPerc, maxClipPerc, minRange, maxRange)]);
    },
    computeUnscaledRadialBoundingTriangles(minAngle, maxAngle) {
      const res = []; // Triangle[]

      // Two triangles per quadrant
      for (let triId = 0; triId < 8; ++triId) {
        const tri = this.computeRawSliceMaskTriangle(triId, minAngle, maxAngle);
        if (tri) {
          res.push(tri);
        }
      }
      return res;
    },
    curveToPath(curve) {
      return `M${curve[0].join(' ')} C${curve[1].join(' ')} ${curve[2].join(' ')} ${curve[3].join(' ')}`;
    },
    transformPoint(pt, scale, offset) {
      return [pt[0] * scale + offset[0], pt[1] * scale + offset[1]];
    },
    transformTriangles(triangles, scale, offset) {
      return triangles.map(tri => tri.map(pt => this.transformPoint(pt, scale, offset)));
    },
    computeTextLength(text) {
      const el = this.$refs['text-test-element']; // SVGTextElement
      if (!el) {
        console.warn('Gem could not find text test element');
        return 0;
      }

      el.textContent = text;
      return el.getComputedTextLength();
    },
  },
  async mounted() {
    // eslint-disable-next-line no-plusplus
    this.id = globalIdAlloc++;

    let ticks = 0;
    while (this && (!this.$refs.svg || !this.$refs['text-test-element'])) {
      if (ticks > 100) break;
      await this.$nextTick();
      // eslint-disable-next-line no-plusplus
      ++ticks;
    }

    this.loaded = true;
    this.resizeSensor = new ElementQueries.ResizeSensor(this.$refs.svg, () => {
      // this.$refs.svg is HTMLDivElement
      this.svgWidth = (this.$refs.svg).clientWidth;
      this.svgHeight = (this.$refs.svg).clientHeight;
    });

    this.onValueChanged();
  },
  beforeDestroy() {
    if (this.resizeSensor) this.resizeSensor.detach();
    this.runningAnimIdx = -1;
  },
};
</script>

<style scoped lang="scss">
.fill-container {
  width: 100%;
  height: 100%;
  position: relative;
}
</style>



// WEBPACK FOOTER //
// src/components/game/ui/CapabilityWheel.vue